import 'consumerweb-shim';
import 'adsinterceptor';
import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';
import MediaQuery from 'react-responsive';
import { Header, Footer } from 'marketing-components';
import {
  CaptionText,
  HeadingText,
  BodyText,
  Container,
  Row,
  Col,
  PAYPAL_THEME,
} from '@paypalcorp/pp-react';
import { ThemeProvider } from '@emotion/react';
import glamorous from 'glamorous';
import CategoryTiles from '../components/categoryTiles/categoryTiles';
import GivingSummaryCard from '../components/givingSummaryCard/givingSummaryCard';
import * as analytics from '../lib/analytics';
import isEmpty from 'lodash/isEmpty';

import CharityList from '../components/charityList';
import HubSocialShare from '../components/HubSocialShare';
import Confirmation from '../components/confirmation';
import CheckoutError from '../components/checkoutError';
import DonationSteps from '../components/donation-steps';
import AdBanner from '../components/adBanner/adBanner';
import { GivingZoneCarousel } from '@paypalcorp/giving-fundraising';
import {
  WideRowComponent,
  PromoMessageCarousel,
} from '../redesignComponents/promoMessageCarousel';
import content from '../lib/i18n';
import { isPPGF } from '../lib/charity';
import styles from './hubLanding.less';

import { DataContext } from '../lib/context';
const i18n = content('hubLanding');

if (window.PAYPAL && window.PAYPAL.analytics) {
  window.PAYPAL.analytics.startClientErrorTracking();
}

import { HUB_PAGE_GROUP, HUB_PAGE_LANDING } from '../lib/fptiTrackingVariables';
import FastAverageColor from 'fast-average-color';
import { getHeaderClassName, getHeaderProps } from '../lib/header';
import SearchBar from '../redesignComponents/searchBar/searchBar';
import BrandedCampaign from '../components/brandedCampaign/brandedCampaign';
import { StepperModule } from '../redesignComponents/stepperModule';

const LegalText = glamorous(CaptionText)({
  '& a': {
    color: PAYPAL_THEME.sysColorSecondaryMain,
    fontFamily: PAYPAL_THEME.sysTypographyBodyStrongFamily,
  },
});

class Page extends React.Component {
  static propTypes = {
    csrf: PropTypes.string,
    featured: PropTypes.array,
    categories: PropTypes.array,
    donationInfo: PropTypes.object,
    authState: PropTypes.string,
    contentCountry: PropTypes.string,
    elmo: PropTypes.object,
  };

  static childContextTypes = {
    csrf: PropTypes.string,
  };

  constructor(props, context) {
    super(props, context);

    var initialState = {
      fptiInfo: {
        pgrp: HUB_PAGE_GROUP,
        page: HUB_PAGE_LANDING,
      },
      isMobile: false,
    };
    this.state = initialState;
    this.imgRef = React.createRef();
  }

  parseFPTIInfo = (fptiData) => {
    var nameValues = [];
    var pairs = decodeURIComponent(fptiData).split('&');
    var i;
    for (i = 0; i < pairs.length; i++) {
      var parts = pairs[i].split('=');
      nameValues[parts[0]] = parts[1];
    }
    return nameValues;
  };

  componentDidMount() {
    let isMobile = false;
    if (/Android|webOS|iPhone|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
      isMobile = true;
      this.setState({
        isMobile,
      });
    }

    // to replace the default /signin href from marketing header
    // we need it to go back to npo page
    const marketingULButton = document.getElementById('ul-btn') || {};
    marketingULButton.href = this.props.unifiedLoginURL;

    analytics.instance.recordEvent(analytics.events.DW_GIVING_SCREEN_SHOWN, {
      e: 'im',
      item_name: 'dw_giving',
      tile_id: 'giving_hub',
      card_type: 'giving_hub',
      scope: 'screen',
      account_type: this.props.authState === 'ANONYMOUS' ? 'guest' : 'member',
      acct_cntry: this.props.contentCountry,
      event_source: analytics.getEventSource(isMobile),
    });
  }

  getChildContext() {
    return {
      csrf: this.props.csrf,
    };
  }

  handleAnalytics = (click) => {
    analytics.trackLink(
      click,
      this.state.fptiInfo.pgrp,
      this.state.fptiInfo.page,
      'link'
    );
  };

  renderFooter = () => {
    const {
      content: { content: footerContent, config = {} },
    } = JSON.parse(document.getElementById('data-footer').innerHTML);
    const { localeCountry } = footerContent;

    return (
      <Footer
        config={config}
        content={footerContent}
        localeCountry={localeCountry}
      />
    );
  };

  renderHeader = () => {
    const dataHeader = JSON.parse(
      document.getElementById('data-header').innerHTML
    );

    const props = getHeaderProps(this.props.authState, dataHeader.content);

    return (
      <div className={getHeaderClassName('v3')}>
        <Header {...props} />
      </div>
    );
  };

  getConfirmationModalText = () => {
    let title = i18n('label.conf-title');
    let subTitle = i18n('label.conf-subTitle');
    let buttonText = i18n('label.conf-buttonText');
    let copyText = i18n('label.conf-copyText');
    let inviteText = i18n('label.conf-invite');
    try {
      const { personalization } = JSON.parse(
        document.getElementById('data-props').innerHTML
      );
      if (!isEmpty(personalization['conf-title'])) {
        title = personalization['conf-title'];
      }
      if (!isEmpty(personalization['conf-subTitle'])) {
        subTitle = personalization['conf-subTitle'];
      }
      if (!isEmpty(personalization['conf-buttonText'])) {
        buttonText = personalization['conf-buttonText'];
      }
      if (!isEmpty(personalization['conf-copyText'])) {
        copyText = personalization['conf-copyText'];
      }
    } catch (e) {
      // Error should not be handled because text is already set
    }
    return {
      title,
      subTitle,
      buttonText,
      copyText,
      inviteText,
      confSocialNeeded: false,
      socialShareNeeded: false,
    };
  };

  /**
   * Sets the hero section background color based on the "dominant" color in the
   * loaded image. It also sets the Title and Subtitle font color to one with
   * high contrast (either black or white).
   */
  setCorrespondingColors = () => {
    const fac = new FastAverageColor();
    const img = this.imgRef.current;

    fac.getColorAsync(img, { algorithm: 'dominant' }).then((color) => {
      // `value` is an array with RGBA values between 0 and 255
      const isTransparent = color.value[3] < 128;

      this.setState({
        backgroundColor:
          i18n.getWithoutDefault('label.hero-background-color') || color.hexa,
        fontColor:
          i18n.getWithoutDefault('label.hero-font-color') ||
          (color.isLight || isTransparent
            ? PAYPAL_THEME.sysColorBackgroundHighContrast
            : PAYPAL_THEME.sysColorBackgroundSurface),
      });
    });
  };

  getCarouselData = (tile, type) => {
    return this.props.pageLayoutData
      ? this.props.pageLayoutData.find(
          (data) => data.tileType === tile && data.carouselType === type
        )
      : {};
  };

  extractModuleData = (tile, type) => {
    const carouselData = this.getCarouselData(tile, type);
    if (!carouselData) {
      return false;
    }

    const { cardContent } = carouselData;
    return cardContent.length > 0;
  };

  mapTileFPTIInfo = (eventType, order) => {
    return {
      e: eventType === 'impression' ? 'im' : 'av',
      state: eventType === 'impression' ? 'populated' : 'loaded',
      account_type: this.props.authState === 'ANONYMOUS' ? 'guest' : 'member',
      acct_cntry: this.props.contentCountry,
      lcid: '-',
      order,
    };
  };

  onTileShow = (pageIndex, data, numOfVisibleItems, order) => {
    if (!pageIndex.includes('-')) {
      const tilesToRecordFPTI = data.slice(
        numOfVisibleItems * (pageIndex - 1),
        numOfVisibleItems * pageIndex
      );

      if (tilesToRecordFPTI && tilesToRecordFPTI.length > 0) {
        tilesToRecordFPTI.forEach((tile) => {
          const fpti = JSON.parse(tile.instrumentationData)[0];
          analytics.instance.recordEvent(
            analytics.events.DW_GIVING_TILE_SHOWN,
            {
              ...this.mapTileFPTIInfo('impression', order),
              ...fpti,
            }
          );
        });
      }
    } else {
      const tileIndex = Number.parseInt(pageIndex.split('-')[1], 10);
      const fpti = JSON.parse(data[tileIndex - 1].instrumentationData)[0];
      analytics.instance.recordEvent(analytics.events.DW_GIVING_TILE_SHOWN, {
        ...this.mapTileFPTIInfo('impression', order),
        ...fpti,
      });
    }
  };

  onTileLoad = (data, order) => {
    if (data) {
      const fpti = JSON.parse(data.instrumentationData)[0];
      analytics.instance.recordEvent(analytics.events.DW_GIVING_TILE_LOADED, {
        ...this.mapTileFPTIInfo('loaded', order),
        ...fpti,
      });
    }
  };

  mapFPTIInfo = (tileType, carouselType, eventType) => {
    let fptiModule = 'trending_fundraiser';
    let fptiOrder = 5;

    if (tileType === 'Charity') {
      switch (carouselType) {
        case 'Trending':
          fptiModule = 'trending_charity';
          fptiOrder = 7;
          break;
        case 'Featured':
          fptiModule = 'featured_charities';
          fptiOrder = 3;
          break;
        case 'Themed':
          fptiModule = 'charity_theme';
          fptiOrder = 4;
          break;

        default:
          break;
      }
    }
    return {
      e: eventType === 'impression' ? 'im' : 'av',
      state: eventType === 'impression' ? 'populated' : 'loaded',
      item_name: 'dw_giving',
      scope: 'module',
      account_type: this.props.authState === 'ANONYMOUS' ? 'guest' : 'member',
      acct_cntry: this.props.contentCountry,
      card_type: fptiModule,
      tile_id: fptiModule,
      lcid: '-', // TODO Check how to get the lcid
      order: fptiOrder,
      module: fptiModule,
    };
  };

  onShow = (tileType, carouselType) => {
    analytics.instance.recordEvent(analytics.events.DW_GIVING_MODULE_SHOWN, {
      ...this.mapFPTIInfo(tileType, carouselType, 'impression'),
    });
  };

  onLoad = (tileType, carouselType) => {
    analytics.instance.recordEvent(analytics.events.DW_GIVING_MODULE_LOADED, {
      ...this.mapFPTIInfo(tileType, carouselType, 'loaded'),
    });
  };

  renderCarousel = (
    tile,
    type,
    numOfVisibleItemsCharities,
    numOfVisibleItemsCharitiesFundraisers
  ) => {
    const carouselData = this.getCarouselData(tile, type);

    if (!carouselData) {
      return null;
    }

    const { cardContent, title, tileType, carouselType } = carouselData;
    const isTrendingFundraiserCarousel =
      carouselType === 'Trending' && tileType === 'Fundraiser';
    const isTrendingCharityCarousel =
      carouselType === 'Trending' && tileType === 'Charity';

    let href = '';

    if (carouselType === 'Trending') {
      href = `${coBrand}hub?keywords=&page=1&pageSize=24&sortBy=trending&type=${
        tileType === 'Charity' ? 'charities' : 'fundraisers'
      }`;
    }
    const numOfVisibleItems =
      tileType === 'Charity'
        ? numOfVisibleItemsCharities
        : numOfVisibleItemsCharitiesFundraisers;

    let carouselOrder = 5;
    if (tileType === 'Charity') {
      carouselOrder = carouselType === 'Trending' ? '7' : '3';
    }

    let typeTile = tileType;
    if (isTrendingFundraiserCarousel && cardContent.length > 0) {
      typeTile =
        cardContent[0].organismName === 'dw_gn_fundraiser_card_medium'
          ? 'Fundraiser'
          : 'FundraiserLarge';
    }

    const showMoreToTrendingFundraisers =
      isTrendingFundraiserCarousel && cardContent.length > 3;

    const showMoreToTrendingCharities =
      isTrendingCharityCarousel && cardContent.length > 6;

    return (
      carouselData &&
      cardContent.length > 0 && (
        <div
          onClick={(event) =>
            analytics.onTileClick(event, {
              contentCountry: this.props.contentCountry,
              authState:
                this.props.authState === 'ANONYMOUS' ? 'guest' : 'member',
              order: carouselOrder,
            })
          }
        >
          <GivingZoneCarousel
            showLink={
              showMoreToTrendingFundraisers ||
              showMoreToTrendingCharities ||
              false
            }
            href={href}
            linkText={i18n('label.see-more')}
            numOfVisibleItems={numOfVisibleItems}
            cardContent={cardContent}
            title={title}
            openNewTab={true}
            domain='/'
            type={typeTile}
            isLoading={false}
            onTileShow={(index) => {
              this.onTileShow(
                index,
                cardContent,
                numOfVisibleItems,
                carouselOrder
              );
            }}
            onTileLoad={(data) => this.onTileLoad(data, carouselOrder)}
            onLoad={() => this.onLoad(tile, type)}
            onShow={() => this.onShow(tile, type)}
          />
        </div>
      )
    );
  };

  renderResponsiveCarousel = (tileType, carouselType) => {
    return (
      <>
        <MediaQuery minWidth={1152}>
          {this.renderCarousel(tileType, carouselType, 6, 3)}
        </MediaQuery>
        <MediaQuery maxWidth={1152} minWidth={1025}>
          {this.renderCarousel(tileType, carouselType, 5, 3)}
        </MediaQuery>
        <MediaQuery maxWidth={1024} minWidth={800}>
          {this.renderCarousel(tileType, carouselType, 4, 2)}
        </MediaQuery>
        <MediaQuery maxWidth={799} minWidth={630}>
          {this.renderCarousel(tileType, carouselType, 3, 2)}
        </MediaQuery>
        <MediaQuery maxWidth={629}>
          {this.renderCarousel(tileType, carouselType, 2, 1)}
        </MediaQuery>
      </>
    );
  };

  renderHubUnifiedExperience() {
    const { segmentCodes = [] } = this.props;
    const hasInsightsSegment = segmentCodes.includes(
      'ALL_20230208_has_giving_donation'
    );
    const showInsights =
      this.props.supportedProduct === 'all' &&
      !isEmpty(this.props.givingInsights);

    const isTrendingModule = this.extractModuleData('Fundraiser', 'Trending');
    const isThemeModule = this.extractModuleData('Charity', 'Themed');

    return (
      <>
        {showInsights && (
          <Row>
            <Col md={12} lg={4}>
              <GivingSummaryCard
                i18n={i18n}
                csrf={csrf}
                supportedProduct={this.props.supportedProduct}
                preferredCharity={this.props.preferredCharity}
                isCountrySetPreferredSupported={
                  this.props.isCountrySetPreferredSupported
                }
                givingInsights={this.props.givingInsights}
                authState={this.props.authState}
                cdnHost={this.props.cdnHost}
                contentCountry={this.props.contentCountry}
                isEngaged={hasInsightsSegment}
              />
            </Col>
            <Col md={12} lg={8}>
              <AdBanner
                size='sm'
                fptiInfo={this.state.fptiInfo}
                i18n={i18n}
                cdnHost={this.props.cdnHost}
              />
            </Col>
          </Row>
        )}
        {this.props.supportedProduct !== 'fundraisers' && (
          <Row>
            <Col className={styles.modulePadding}>
              {this.renderResponsiveCarousel('Charity', 'Featured')}
            </Col>
          </Row>
        )}
        {this.renderBrandedCampaignsModule()}

        {this.props.supportedProduct !== 'charities' && isTrendingModule && (
          <Row>
            <Col className={styles.modulePadding}>
              {this.renderResponsiveCarousel('Fundraiser', 'Trending')}
            </Col>
          </Row>
        )}
        {!showInsights &&
          this.props.isBannerEnabled &&
          this.props.supportedProduct !== 'charities' &&
          i18n('label.banner-title').length > 1 && (
            <div className={styles.modulePadding}>
              <WideRowComponent>
                <div>
                  <MediaQuery minWidth={481}>
                    <Container>
                      <Row>
                        <Col>{this.renderStepsModule()}</Col>
                      </Row>
                    </Container>
                  </MediaQuery>
                  <MediaQuery maxWidth={480}>
                    {this.renderStepsModule(true)}
                  </MediaQuery>
                </div>
              </WideRowComponent>
            </div>
          )}
        {this.props.supportedProduct !== 'fundraisers' && (
          <Row>
            <Col className={styles.modulePadding}>
              {this.renderResponsiveCarousel('Charity', 'Trending')}
            </Col>
          </Row>
        )}
        {isThemeModule && (
          <Row>
            <Col className={styles.modulePadding}>
              {this.renderPromoMessageCarousel('Charity', 'Themed')}
            </Col>
          </Row>
        )}
      </>
    );
  }

  renderPromoMessageCarousel = (tile, type) => {
    const carouselData = this.getCarouselData(tile, type);

    if (!carouselData) {
      return null;
    }
    const { cardContent, promoMessageData } = carouselData;
    const {
      detailed_title,
      detailed_subtitle,
      detailed_image,
      detailed_background_color,
    } = promoMessageData;

    // Since we don't have all the assets for this section, this validation will prevent to render the carousel without all the data needed
    if (
      !detailed_title ||
      !detailed_subtitle ||
      !detailed_image ||
      !detailed_background_color
    ) {
      return null;
    }

    return (
      cardContent &&
      cardContent.length > 0 && (
        <PromoMessageCarousel
          featuredImageUrl={detailed_image}
          backgroundColor={detailed_background_color}
          isFeatureImageToTheLeft={false}
          hasImageOffset={false}
          fontColor='#FFFFFF'
          cardContent={cardContent}
          openNewTab={true}
          domain='/'
          header={detailed_title}
          subHeader={detailed_subtitle}
          isLoading={false}
          authState={authState}
          contentCountry={this.props.contentCountry}
          onLoad={this.onLoad}
          onShow={this.onShow}
          onTileLoad={this.onTileLoad}
          onTileShow={this.onTileShow}
          tileType={tile}
          order={4}
          carouselType={type}
        />
      )
    );
  };

  renderBrandedCampaignsModule = () => {
    const data = this.getCarouselData('Fundraiser', 'Branded');
    if (data && data.cardContent) {
      const cardContent = data.cardContent;
      return (
        cardContent &&
        cardContent.length > 0 &&
        cardContent.map((brandedCampaign) => {
          return (
            <WideRowComponent>
              <div style={{ marginBottom: '24px' }}>
                <Container>
                  <Row>
                    <Col>
                      <BrandedCampaign
                        fptiInfo={this.state.fptiInfo}
                        i18n={i18n}
                        cdnHost={this.props.cdnHost}
                        data={brandedCampaign}
                        isMobile={this.state.isMobile}
                      />
                    </Col>
                  </Row>
                </Container>
              </div>
            </WideRowComponent>
          );
        })
      );
    }
  };

  renderStepsModule = (isMobileView = false) => {
    const data = this.props.pageLayoutData.find(
      (data) =>
        data.tileType === '' &&
        data.carouselType === '' &&
        data.touchpoint === 'ConsumerApp_Givinghub_StepsModule_Touchpoint'
    );
    if (data && data.cardContent.length > 0) {
      const cardContent = data.cardContent;
      const {
        module,
        imageConfiguration,
        heading,
        stepsContent,
        backgroundColor,
        cta,
        instrumentation,
        subheading,
      } = cardContent[0];
      return (
        <StepperModule
          image={
            isMobileView
              ? imageConfiguration[0].image_url_mweb
              : imageConfiguration[0].image_url_web
          }
          backgroundImageColor={imageConfiguration[0].bg_color}
          backgroundColor={backgroundColor}
          setImageToTheLeft={imageConfiguration[0].image_position === 'left'}
          stepsContent={stepsContent}
          header={heading}
          subHeader={subheading}
          buttonText={cta[0].title}
          linkUrl={cta[0].web_deeplink}
          isPrimaryModule={module === 'primary'}
          instrumentation={instrumentation.length ? instrumentation[0] : {}}
          authState={this.props.authState}
          contentCountry={this.props.contentCountry}
          elmo={this.props.elmo}
        />
      );
    }
    return (
      i18n('label.banner-title').length > 1 && (
        <AdBanner
          fptiInfo={this.state.fptiInfo}
          i18n={i18n}
          cdnHost={this.props.cdnHost}
        />
      )
    );
  };

  // TODO: hardcoded image url have to be replaced ln 226 and 274 with PS source
  render() {
    const heroImageHeader = i18n('label.hub-title');
    const heroImageSubheader = i18n('label.hub-subtitle');
    const npoBaseUrl = '/fundraiser/charity/';
    const confirmationModalText = this.getConfirmationModalText();
    let marginStyle = '';
    if (this.props.pageLayoutData.length > 0) {
      marginStyle =
        this.props.pageLayoutData[0].cardContent.length > 0
          ? 'charityPanelMargin'
          : '';
    }

    let charityList = (
      <CharityList
        {...this.props}
        fptiInfo={this.state.fptiInfo}
        i18n={i18n}
        charities={this.props.suggestedCharties}
      />
    );
    return (
      <>
        <div> {this.props.mppHeader && this.renderHeader()}</div>
        <div>
          <div
            style={{
              width: '100%',
              backgroundColor: this.state.backgroundColor,
            }}
          >
            <Container>
              <MediaQuery minWidth={1024}>
                <Row>
                  <Col
                    xs={6}
                    style={{
                      display: 'flex',
                      marginTop: '104px',
                    }}
                  >
                    <div id='heroContents'>
                      <HeadingText
                        size='hero'
                        style={{ color: this.state.fontColor }}
                        dangerouslySetInnerHTML={{
                          __html: heroImageHeader,
                        }}
                      />
                      <BodyText
                        style={{
                          color: this.state.fontColor,
                          padding: '16px 0 0 0',
                        }}
                        dangerouslySetInnerHTML={{
                          __html: heroImageSubheader,
                        }}
                      />
                    </div>
                  </Col>
                  <Col
                    xs={6}
                    style={{
                      textAlign: 'center',
                      position: 'revert',
                      alignSelf: 'end',
                    }}
                  >
                    <img
                      ref={this.imgRef}
                      alt='heroImage'
                      src={i18n('label.hero_image_url')}
                      style={{
                        height: '524px',
                        float: 'right',
                      }}
                      onLoad={() => this.setCorrespondingColors()}
                      crossOrigin='Anonymous'
                    />
                  </Col>
                </Row>
              </MediaQuery>
              <MediaQuery maxWidth={1023}>
                <Row>
                  <Col
                    style={{
                      paddingTop: '16px',
                    }}
                  >
                    <div>
                      <HeadingText
                        size='lg'
                        style={{ color: this.state.fontColor }}
                        dangerouslySetInnerHTML={{
                          __html: heroImageHeader,
                        }}
                      />
                      <BodyText
                        style={{
                          color: this.state.fontColor,
                          paddingTop: '16px',
                        }}
                        dangerouslySetInnerHTML={{
                          __html: heroImageSubheader,
                        }}
                      />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col style={{ textAlign: 'center' }}>
                    <img
                      ref={this.imgRef}
                      alt='heroImage'
                      src={i18n('label.hero-mobile-image')}
                      style={{
                        paddingTop: '24px',
                        maxHeight: '600px',
                        maxWidth: '100%',
                      }}
                      onLoad={() => this.setCorrespondingColors()}
                      crossOrigin='Anonymous'
                    />
                  </Col>
                </Row>
              </MediaQuery>
            </Container>
          </div>
          <Container>
            <Row>
              <Col>
                <MediaQuery minWidth={1200}>
                  <div className={styles.searchBoxDiv}>
                    <SearchBar
                      {...this.props}
                      i18n={i18n}
                      isMobile={this.state.isMobile}
                      fptiInfo={this.state.fptiInfo}
                      searchSubmitUrl={`${this.props.coBrand}hub`}
                    />
                  </div>
                </MediaQuery>
              </Col>
            </Row>
            <Row>
              <Col>
                <MediaQuery maxWidth={1199}>
                  <div className={styles.mobileSearchBox}>
                    <SearchBar
                      {...this.props}
                      i18n={i18n}
                      isMobile={this.state.isMobile}
                      fptiInfo={this.state.fptiInfo}
                      searchSubmitUrl={`${this.props.coBrand}hub`}
                    />
                  </div>
                </MediaQuery>
              </Col>
            </Row>
            {this.renderHubUnifiedExperience()}
            {this.props.donationInfo &&
              this.props.donationInfo.donated === 1 && (
                <Confirmation
                  title={confirmationModalText.title}
                  subTitle={confirmationModalText.subTitle}
                  buttonText={confirmationModalText.buttonText}
                  copyText={confirmationModalText.copyText}
                  inviteText={confirmationModalText.inviteText}
                  fptiInfo={this.state.fptiInfo}
                  npoUrl={this.props.donationInfo.url}
                  npid={this.props.donationInfo.npid}
                  cdnHost={this.props.cdnHost}
                  campaignSocialShare={false}
                  i18n={i18n}
                />
              )}
            {this.props.donationInfo &&
              this.props.donationInfo.donated === 0 && (
                <CheckoutError
                  i18n={i18n}
                  fptiInfo={this.state.fptiInfo}
                  cdnHost={this.props.cdnHost}
                />
              )}
            <Row>
              <Col>
                {!charityListUnderThreshold ||
                this.props.supportedProduct === 'fundraisers' ? (
                  <CategoryTiles
                    id={'charityCategories'}
                    categories={this.props.categories}
                    fundraiserCategories={this.props.fundraiserCategories}
                    coBrand={`${coBrand}hub`}
                    fptiInfo={this.state.fptiInfo}
                    maxTextLength={40}
                    type={this.props.supportedProduct}
                    i18n={i18n}
                    authState={this.props.authState}
                    contentCountry={this.props.contentCountry}
                  />
                ) : (
                  <div
                    className={`panel ${styles.charityPanel} ${styles[marginStyle]}`}
                  >
                    <HeadingText
                      as='h3'
                      size='sm'
                      className={styles.charityListHeader}
                    >
                      {i18n('label.suggest-charities')}
                    </HeadingText>
                    {charityList}
                  </div>
                )}
              </Col>
            </Row>
            {isPPGF(allowedCharityType) && (
              <DonationSteps i18n={i18n} cdnHost={this.props.cdnHost} />
            )}

            <Row>
              <Col>
                {/* {isPPGF(allowedCharityType) && (
      <FAQBanner fptiInfo={this.state.fptiInfo} i18n={i18n} />
    )} */}
              </Col>
            </Row>
            <Row>
              <Col>
                <HubSocialShare
                  i18n={i18n}
                  url={window.location.href}
                  fptiInfo={this.state.fptiInfo}
                  fbDesc={i18n('label.fb-share-msg')}
                  fbTitle={i18n('label.fb-share-name')}
                  cdnHost={this.props.cdnHost}
                />
              </Col>
            </Row>
          </Container>
          {this.props.supportedProduct !== 'fundraisers' && (
            <div
              id={'gift'}
              className={'container externalLinkText'}
              style={{ marginTop: '20px' }}
            >
              <LegalText
                dangerouslySetInnerHTML={{
                  __html: i18n('label.legal-footer-default'),
                }}
                as='p'
                size='xs'
              />
            </div>
          )}
          {this.props.mppFooter && this.renderFooter()}
        </div>
      </>
    );
  }
}

const hub = JSON.parse(document.getElementById('data-hub').innerHTML);

const data = JSON.parse(document.getElementById('data-props').innerHTML);
const authState = data.authState;
const resources = JSON.parse(
  document.getElementById('data-resources').innerHTML
);
const supportedProduct = data.supportedProduct;
const charityListUnderThreshold = data.charityListUnderThreshold;
const preferredCharityNpid = data.preferredCharityNpid;
const preferredCharity = data.preferredCharity;
const redirectPreferredNpid = data.redirectPreferredNpid;
const query = data.query;
const allowedCharityType = data.allowedCharityType;
const charities = data.charities;
const maxCharityResults = data.maxCharityResults;
const suggestedCharties = data.suggestedCharties;
const featured = data.featured;
const pageLayoutData = data.pageLayoutData;
const recommended = data.recommended || {};
const nearBy = data.nearBy;
const states = data.states;
const categories = data.categories;
const fundraiserCategories = data.fundraiserCategories;
const labels = data.labels;
const donation = data.donation;
const donationInfo = data.donationInfo;
const mppFooter = data.mppFooter;
const mppHeader = data.mppHeader;
const amounts = data.amounts;
const unifiedLoginURL = data.unifiedLoginURL;
const isCountrySetPreferredSupported = data.isCountrySetPreferredSupported;
const userType = data.userType;
const cdnHost = data.cdnHost;
const domain = data.domain;
const isBannerEnabled = data.isBannerEnabled;
const givingInsights = data.givingInsights;
const segmentCodes = data.segmentCodes;
const searchUrl = document.getElementById('data-search-url').innerHTML;
const csrf = document.getElementById('data-csrf').innerHTML;
const fptiInfo = JSON.parse(
  document.getElementById('data-fpti-info').innerHTML
);
const coBrand = document.getElementById('data-co-brand').innerHTML;
const charityUrl = `${coBrand}charity`;
const deviceType = JSON.parse(
  document.getElementById('data-deviceType').innerHTML
);
const locale = JSON.parse(document.getElementById('data-locale').innerHTML);
const hubUrl = `https://www.${domain}${coBrand}hub`;
const elmo = data.elmo;

ReactDOM.render(
  <DataContext.Provider value={data}>
    <ThemeProvider theme={PAYPAL_THEME}>
      <Page
        supportedProduct={supportedProduct}
        query={query}
        charities={charities}
        maxCharityResults={maxCharityResults}
        isCountrySetPreferredSupported={isCountrySetPreferredSupported}
        preferredCharityNpid={preferredCharityNpid}
        preferredCharity={preferredCharity}
        authState={authState}
        contentCountry={locale.country}
        redirectPreferredNpid={redirectPreferredNpid}
        userType={userType}
        cdnHost={cdnHost}
        domain={domain}
        isBannerEnabled={isBannerEnabled}
        charityListUnderThreshold={charityListUnderThreshold}
        suggestedCharties={suggestedCharties}
        featured={featured}
        pageLayoutData={pageLayoutData}
        amounts={amounts}
        nearBy={nearBy}
        csrf={csrf}
        searchUrl={searchUrl}
        charityUrl={charityUrl}
        fptiInfo={fptiInfo}
        categories={categories}
        fundraiserCategories={fundraiserCategories}
        states={states}
        coBrand={coBrand}
        recommended={recommended}
        resources={resources}
        labels={labels}
        donation={donation}
        deviceType={deviceType}
        donationInfo={donationInfo}
        hubUrl={hubUrl}
        hub={hub}
        unifiedLoginURL={unifiedLoginURL}
        mppFooter={mppFooter}
        mppHeader={mppHeader}
        givingInsights={givingInsights}
        segmentCodes={segmentCodes}
        elmo={elmo}
      />
    </ThemeProvider>
  </DataContext.Provider>,
  document.getElementById('page')
);
